import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import TextField from "@/components/ui/Textfield";
import Dropdown from "@/components/ui/DropDown";
import Button from "@/components/ui/Button";
import useAuthStore from "@/store/authStore";
import useGlobalStore from "@/store/globalStore";
import useRecoveryStore from "@/pages/settings/store/recoveryStore";

interface SecurityFormData {
  recoveryEmail: string;
  phoneCountry: string;
  phoneNumber: string;
  emailOtp: string;
  smsCode: string;
}

const SignInSecurity: React.FC = () => {
  const navigate = useNavigate();
  const { media_agency_information } = useAuthStore();
  const { showSuccess, showError } = useGlobalStore();
  const {
    isLoading,
    emailOtpSent,
    smsCodeSent,
    emailVerified,
    smsVerified,
    sendRecoveryEmail,
    sendRecoverySms,
    verifyRecoveryEmail,
    verifyRecoverySms,
    resetState,
  } = useRecoveryStore();

  const [formData, setFormData] = useState<SecurityFormData>({
    recoveryEmail: "",
    phoneCountry: "+1",
    phoneNumber: "",
    emailOtp: "",
    smsCode: "",
  });

  useEffect(() => {
    // Initialize form with existing recovery data if available
    if (media_agency_information?.recovery_email) {
      setFormData((prev) => ({
        ...prev,
        recoveryEmail: media_agency_information.recovery_email || "",
      }));
    }
    if (media_agency_information?.recovery_phone) {
      setFormData((prev) => ({
        ...prev,
        phoneNumber: media_agency_information.recovery_phone || "",
      }));
    }
  }, [media_agency_information]);

  const handleChange = (field: keyof SecurityFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSendRecoveryEmail = async () => {
    if (!formData.recoveryEmail) {
      showError("Please enter a recovery email");
      return;
    }

    if (!media_agency_information?.id) {
      showError("Agency information not found");
      return;
    }

    try {
      await sendRecoveryEmail(
        media_agency_information.id,
        formData.recoveryEmail
      );
      showSuccess("OTP sent to your recovery email!");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to send OTP");
    }
  };

  const handleVerifyRecoveryEmail = async () => {
    if (!formData.emailOtp) {
      showError("Please enter the OTP code");
      return;
    }

    if (!media_agency_information?.id) {
      showError("Agency information not found");
      return;
    }

    try {
      await verifyRecoveryEmail(media_agency_information.id, formData.emailOtp);
      showSuccess("Recovery email verified successfully!");
    } catch (error) {
      showError(
        error instanceof Error ? error.message : "Failed to verify OTP"
      );
    }
  };

  const handleSendTestCode = async () => {
    if (!formData.phoneNumber) {
      showError("Please enter a phone number");
      return;
    }

    if (!media_agency_information?.id) {
      showError("Agency information not found");
      return;
    }

    // Combine country code with phone number
    const fullPhoneNumber = `${formData.phoneCountry}${formData.phoneNumber}`;

    try {
      await sendRecoverySms(media_agency_information.id, fullPhoneNumber);
      showSuccess("Test code sent to your phone!");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to send SMS");
    }
  };

  const handleVerifyRecoverySms = async () => {
    if (!formData.smsCode) {
      showError("Please enter the SMS code");
      return;
    }

    if (!media_agency_information?.id) {
      showError("Agency information not found");
      return;
    }

    try {
      await verifyRecoverySms(media_agency_information.id, formData.smsCode);
      showSuccess("Phone number verified successfully!");
    } catch (error) {
      showError(
        error instanceof Error ? error.message : "Failed to verify SMS code"
      );
    }
  };

  const handleSave = () => {
    if (!emailVerified && formData.recoveryEmail) {
      showError("Please verify your recovery email before saving");
      return;
    }

    if (!smsVerified && formData.phoneNumber) {
      showError("Please verify your phone number before saving");
      return;
    }

    showSuccess("Recovery settings saved successfully!");
    resetState();
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-2">Account Preferences</h1>
      <h2 className="text-xl font-medium mb-6 flex items-center mt-10">
        <ChevronLeft
          className="w-6 h-6 text-[#EA33C0] mr-4 cursor-pointer"
          onClick={() => navigate("/account")}
        />
        Sign-In Security
      </h2>

      <div className="border-t border-[#EA33C0] pt-8">
        <div className="mb-8">
          <h3 className="text-base font-medium mb-2">Recovery Email</h3>
          <p className="text-gray-600 text-sm mb-4">
            This email is used to recover your business account. This email
            should not be a work related email.
          </p>
          <div className="flex gap-4 items-center mb-4">
            <TextField
              type="email"
              placeholder="Enter email address"
              value={formData.recoveryEmail}
              onChange={(e) => handleChange("recoveryEmail", e.target.value)}
              className="w-1/2"
              disabled={emailVerified || isLoading}
            />
            {!emailVerified && (
              <Button
                type="secondary"
                onClick={handleSendRecoveryEmail}
                className="min-w-fit"
                disabled={isLoading || !formData.recoveryEmail || emailOtpSent}
              >
                {isLoading
                  ? "Sending..."
                  : emailOtpSent
                  ? "Code Sent"
                  : "Send Code"}
              </Button>
            )}
            {emailVerified && (
              <span className="text-green-600 text-sm font-medium">
                ✓ Verified
              </span>
            )}
          </div>

          {emailOtpSent && !emailVerified && (
            <div className="flex gap-4 items-center mt-4">
              <TextField
                type="text"
                placeholder="Enter OTP code"
                value={formData.emailOtp}
                onChange={(e) => handleChange("emailOtp", e.target.value)}
                className="w-1/3"
                disabled={isLoading}
              />
              <Button
                type="primary"
                onClick={handleVerifyRecoveryEmail}
                className="min-w-fit"
                disabled={isLoading || !formData.emailOtp}
              >
                {isLoading ? "Verifying..." : "Verify OTP"}
              </Button>
            </div>
          )}
        </div>

        <div className="mb-8">
          <h3 className="text-base font-medium mb-2">
            2-factor Authentication - SMS
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Enable 2-factor authentication as a second layer of security for
            your account. We will use SMS to send a code to your mobile phone
            number. Standard text messaging rates may apply.
          </p>
          <div className="flex gap-4 items-center mb-4">
            <Dropdown
              options={[{ title: "+1 (US)", value: "+1" }]}
              selected={formData.phoneCountry}
              setSelected={(e) => handleChange("phoneCountry", e.toString())}
            />

            <TextField
              type="tel"
              placeholder="123-456-7890"
              value={formData.phoneNumber}
              onChange={(e) => handleChange("phoneNumber", e.target.value)}
              className="w-1/3"
              disabled={smsVerified || isLoading}
            />
            {!smsVerified && (
              <Button
                type="secondary"
                onClick={handleSendTestCode}
                className="min-w-fit"
                disabled={isLoading || !formData.phoneNumber || smsCodeSent}
              >
                {isLoading
                  ? "Sending..."
                  : smsCodeSent
                  ? "Code Sent"
                  : "Send OTP"}
              </Button>
            )}
            {smsVerified && (
              <span className="text-green-600 text-sm font-medium">
                ✓ Verified
              </span>
            )}
          </div>

          {smsCodeSent && !smsVerified && (
            <div className="flex gap-4 items-center mt-4">
              <TextField
                type="text"
                placeholder="Enter SMS code"
                value={formData.smsCode}
                onChange={(e) => handleChange("smsCode", e.target.value)}
                className="w-1/3"
                disabled={isLoading}
              />
              <Button
                type="primary"
                onClick={handleVerifyRecoverySms}
                className="min-w-fit"
                disabled={isLoading || !formData.smsCode}
              >
                {isLoading ? "Verifying..." : "Verify Code"}
              </Button>
            </div>
          )}
        </div>

        <div className="mt-12 border-t pt-8">
          <div className="flex items-center gap-4">
            <Button type="primary" onClick={handleSave}>
              Save Changes
            </Button>
            <span className="text-gray-400 text-sm">
              Note: changes will apply across all your MediaBridge accounts.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInSecurity;
